import { useState } from 'react';
import { statusLook, useTaskStatuses } from '../lib/taskStatuses.js';
import { isOpenTask } from '@ehr/shared';

/**
 * Shared Tasks List + Board views (#27). Extracted from the Tasks page so the
 * same List and Board render both on the full Tasks page and inside the contact
 * drawer's Tasks tab. Presentational only: the caller owns the data and the
 * open / toggle / move / select callbacks.
 */

export type TaskViewStatus = 'open' | 'in_progress' | 'done' | 'cancelled';

/** The task fields the shared views read. Both the Tasks page and the contact
 *  drawer pass rows that satisfy this. */
export interface TaskView {
  id: string;
  title: string;
  status: TaskViewStatus;
  priority: string;
  dueAt: string | null;
  doneAt: string | null;
  contactId: string | null;
  createdById: string;
  assignees: { userId: string }[];
  sharedWith?: string[];
  /** ehr#487: the task's checklist; the row shows how many are ticked. */
  checklist?: { done: boolean }[];
}

export interface TaskViewUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export type TaskLinkGroup = 'all' | 'client' | 'partner';
/** Resolve a task's linked contact to a display name + group (null = unlinked). */
export type TaskContactName = (id: string | null) => { name: string; group: TaskLinkGroup } | null;

// ── palette + columns (V34 parity) ───────────────────────────────────────────
// ehr#411: status names, colours and the board's column order come from Settings → Task Statuses
// (lib/taskStatuses.ts; the built-in Open / In progress / Complete / Cancelled until it loads).
const PRIORITY_COLS: { key: string; label: string }[] = [
  { key: 'urgent', label: 'Urgent' },
  { key: 'high', label: 'High' },
  { key: 'normal', label: 'Normal' },
  { key: 'low', label: 'Low' },
];

// ── date + user helpers (date-only, local) ───────────────────────────────────
// ehr#659: closed = not open, by the one rule every task surface uses (@ehr/shared isOpenTask).
const isClosed = (t: TaskView) => !isOpenTask(t);
const dayFloor = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};
const taskDueDiff = (iso: string) =>
  Math.round((dayFloor(new Date(iso)) - dayFloor(new Date())) / 86400_000);
const listDueLabel = (t: TaskView): string => {
  if (!t.dueAt) return '';
  const diff = taskDueDiff(t.dueAt);
  if (!isClosed(t)) {
    if (diff < 0) return `${Math.abs(diff)}d overdue`;
    if (diff === 0) return 'Due today';
    if (diff === 1) return 'Due tomorrow';
    if (diff <= 2) return `In ${diff}d`;
  }
  return new Date(t.dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const dueState = (t: TaskView): '' | 'overdue' | 'today' | 'soon' => {
  if (!t.dueAt || isClosed(t)) return '';
  const diff = taskDueDiff(t.dueAt);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 2) return 'soon';
  return '';
};
const dueStripeClass = (t: TaskView): string => {
  if (!t.dueAt || isClosed(t)) return '';
  const diff = taskDueDiff(t.dueAt);
  if (diff < 0) return 'due-stripe-overdue';
  if (diff === 0 || diff === 1) return 'due-stripe-soon';
  return '';
};

const AVATAR_COLORS = ['#7c3aed', '#0c7be0', '#0e7490', '#d97706', '#1a8a66', '#c0392b', '#527060'];
const avatarColor = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
};
const userName = (u: TaskViewUser) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
const initials = (u: TaskViewUser) => {
  const a = (u.firstName || '').trim();
  const b = (u.lastName || '').trim();
  return ((a[0] || '') + (b[0] || '')).toUpperCase() || (u.email[0] || '?').toUpperCase();
};

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const PersonIcon = ({ size = 11 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="7" r="4" />
    <path d="M5.5 21a6.5 6.5 0 0 1 13 0" />
  </svg>
);

/** ehr#487: a task's checklist completion on its row / card ("2/5" and a small bar); none = nothing. */
function ChecklistProgress({ task }: { task: TaskView }) {
  const items = Array.isArray(task.checklist) ? task.checklist : [];
  if (!items.length) return null;
  const done = items.filter((i) => i?.done).length;
  const all = done === items.length;
  const said = `Checklist: ${done} of ${items.length} done`;
  return (
    <span className={`task-checklist-chip${all ? ' all-done' : ''}`} title={said} aria-label={said}>
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <polyline points="9 11 12 14 22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
      {done}/{items.length}
      <span className="task-checklist-chip-bar" aria-hidden>
        <span style={{ width: `${Math.round((done / items.length) * 100)}%` }} />
      </span>
    </span>
  );
}

/** List-view assignee cell: single avatar+name, or a stacked "bucket of N". */
function AssigneeCell({ task, userById }: { task: TaskView; userById: Map<string, TaskViewUser> }) {
  const ids = task.assignees.map((a) => a.userId);
  if (ids.length === 0) return <span style={{ color: 'var(--text-muted-2)' }}>Unassigned</span>;
  if (ids.length === 1) {
    const u = userById.get(ids[0]!);
    if (!u) return <span style={{ color: 'var(--text-muted-2)' }}>Unknown</span>;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span className="user-avatar tiny" style={{ background: avatarColor(u.id) }}>
          {initials(u)}
        </span>
        {u.firstName || userName(u)}
      </span>
    );
  }
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center' }}
      title={ids.map((id) => (userById.get(id) ? userName(userById.get(id)!) : '?')).join(', ')}
    >
      {ids.slice(0, 3).map((id, i) => {
        const u = userById.get(id);
        return (
          <span
            key={id}
            className="user-avatar tiny"
            style={{
              background: avatarColor(id),
              marginLeft: i === 0 ? 0 : -6,
              border: '1.5px solid var(--white)',
            }}
          >
            {u ? initials(u) : '?'}
          </span>
        );
      })}
      {ids.length > 3 && (
        <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-muted)' }}>
          +{ids.length - 3}
        </span>
      )}
    </span>
  );
}

// ── List view ────────────────────────────────────────────────────────────────
export function TaskListView({
  tasks,
  users,
  contactName,
  onOpen,
  onToggle,
  selectable = false,
  selected = [],
  onToggleSel,
  onToggleAll,
  allSelected = false,
  compact = false,
}: {
  tasks: TaskView[];
  users: TaskViewUser[];
  contactName?: TaskContactName;
  onOpen: (id: string) => void;
  onToggle: (t: TaskView) => void;
  /** Show the leading select checkboxes + Linked-to column (Tasks page). */
  selectable?: boolean;
  selected?: string[];
  onToggleSel?: (id: string) => void;
  onToggleAll?: () => void;
  allSelected?: boolean;
  /** Drawer layout: drop the select + Linked-to columns (7-col grid). */
  compact?: boolean;
}) {
  const looks = useTaskStatuses();
  const userById = new Map(users.map((u) => [u.id, u]));
  return (
    <div className={`tasks-list-view${compact ? ' compact' : ''}`}>
      <div className="tasks-list-row head">
        {!compact && (
          <div>
            {selectable ? (
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                aria-label="Select all"
              />
            ) : null}
          </div>
        )}
        <div />
        <div>Task</div>
        {!compact && <div className="hide-mobile">Linked to</div>}
        <div className="hide-mobile">Due</div>
        <div className="hide-mobile">Assignee</div>
        <div className="hide-mobile">Status</div>
        <div />
        <div />
      </div>
      {tasks.map((t) => {
        const link = contactName ? contactName(t.contactId) : null;
        const ds = dueState(t);
        const dueColor =
          ds === 'overdue'
            ? 'var(--problem)'
            : ds === 'today'
              ? 'var(--caution)'
              : 'var(--text-muted)';
        const st = statusLook(looks, t.status);
        const sel = selected.includes(t.id);
        return (
          <div
            key={t.id}
            className={`tasks-list-row status-${t.status} ${sel ? 'selected' : ''} ${dueStripeClass(t)}`}
            onClick={() => onOpen(t.id)}
          >
            {!compact && (
              <div onClick={(e) => e.stopPropagation()}>
                {selectable ? (
                  <input
                    type="checkbox"
                    checked={sel}
                    onChange={() => onToggleSel?.(t.id)}
                    aria-label="Select task"
                  />
                ) : null}
              </div>
            )}
            <button
              className={`tasks-list-check ${isClosed(t) ? 'done' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(t);
              }}
              aria-label="Toggle complete"
            />
            <div className="tasks-list-title">
              <span className="tasks-list-title-text">{t.title || '(Untitled task)'}</span>
              <ChecklistProgress task={t} />
            </div>
            {!compact && (
              <div className="hide-mobile">
                {link ? (
                  <span className="tasks-list-prospect" title={link.name}>
                    <PersonIcon />
                    <span className="tasks-list-prospect-name">{link.name}</span>
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted-2)' }}>—</span>
                )}
              </div>
            )}
            <div className="hide-mobile">
              {t.dueAt ? (
                <span
                  style={{
                    color: dueColor,
                    fontWeight: ds === 'overdue' || ds === 'today' ? 600 : 400,
                  }}
                >
                  {listDueLabel(t)}
                </span>
              ) : (
                <span style={{ color: 'var(--text-muted-2)' }}>—</span>
              )}
            </div>
            <div className="hide-mobile">
              <AssigneeCell task={t} userById={userById} />
            </div>
            <div className="hide-mobile">
              <span
                className="task-field-value-pill"
                style={{ background: st.bg, color: st.color, borderColor: `${st.color}33` }}
              >
                {st.label}
              </span>
            </div>
            <div className="tasks-list-arrow" onClick={(e) => e.stopPropagation()}>
              <span className="tasks-row-formbtn" aria-hidden>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </span>
            </div>
            <div className="tasks-list-arrow">→</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Board (kanban) view ──────────────────────────────────────────────────────
export function TaskBoardView({
  tasks,
  users,
  contactName,
  onOpen,
  onMove,
  scroll = false,
}: {
  tasks: TaskView[];
  users: TaskViewUser[];
  contactName?: TaskContactName;
  onOpen: (id: string) => void;
  onMove: (taskId: string, status: TaskViewStatus) => void;
  /** Drawer layout: horizontal-scroll columns instead of a 4-up grid. */
  scroll?: boolean;
}) {
  const looks = useTaskStatuses();
  const userById = new Map(users.map((u) => [u.id, u]));
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<TaskViewStatus | null>(null);
  return (
    <div
      className={`tasks-board${scroll ? ' scroll' : ''}`}
      style={
        scroll
          ? { display: 'flex', gap: 12, overflowX: 'auto' }
          : { gridTemplateColumns: `repeat(${looks.length}, minmax(0, 1fr))` }
      }
    >
      {looks
        .map(({ key: id, label, color }) => ({ id, label, color }))
        .map((col) => {
          const items = tasks.filter((t) => t.status === col.id);
          return (
            <div
              key={col.id}
              className="tasks-col"
              style={scroll ? { flex: '0 0 210px', minWidth: 210 } : undefined}
            >
              <div className="tasks-col-head">
                <span className="tasks-col-title">
                  <span className="tasks-col-dot" style={{ background: col.color }} />
                  {col.label}
                </span>
                <span className="tasks-col-count">{items.length}</span>
              </div>
              <div
                className={`tasks-col-body ${dragOverCol === col.id ? 'drag-over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOverCol !== col.id) setDragOverCol(col.id);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverCol(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggingId) onMove(draggingId, col.id);
                  setDraggingId(null);
                  setDragOverCol(null);
                }}
              >
                {items.length === 0 ? (
                  <div className="tasks-col-empty">Drag a task here</div>
                ) : (
                  items.map((t) => {
                    const link = contactName ? contactName(t.contactId) : null;
                    const ds = dueState(t);
                    const prio = PRIORITY_COLS.find((p) => p.key === t.priority);
                    const st = statusLook(looks, t.status);
                    const ids = t.assignees.map((a) => a.userId);
                    return (
                      <button
                        key={t.id}
                        className={`task-card status-${t.status} priority-${t.priority} ${dueStripeClass(t)} ${draggingId === t.id ? 'dragging' : ''}`}
                        draggable
                        onDragStart={() => setDraggingId(t.id)}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDragOverCol(null);
                        }}
                        onClick={() => onOpen(t.id)}
                      >
                        <div className="task-card-title">{t.title || '(Untitled task)'}</div>
                        <div className="task-card-meta">
                          <ChecklistProgress task={t} />
                          {t.dueAt && (
                            <span className={`task-card-due ${ds}`}>
                              <CalendarIcon />
                              {listDueLabel(t)}
                            </span>
                          )}
                          {link && (
                            <span className="task-card-prospect" title={link.name}>
                              <PersonIcon />
                              <span className="task-card-prospect-name">{link.name}</span>
                            </span>
                          )}
                          <span
                            className="task-status-pill"
                            style={{
                              background: st.bg,
                              color: st.color,
                              borderColor: `${st.color}33`,
                            }}
                          >
                            {st.label}
                          </span>
                          <span className={`task-priority-pill p-${t.priority}`}>
                            {prio?.label ?? t.priority}
                          </span>
                        </div>
                        <div className="task-card-foot">
                          <span className="task-card-assignee">
                            {ids.length === 0 ? (
                              'Unassigned'
                            ) : ids.length === 1 ? (
                              (() => {
                                const u = userById.get(ids[0]!);
                                return u ? (
                                  <>
                                    <span
                                      className="user-avatar tiny"
                                      style={{ background: avatarColor(u.id) }}
                                    >
                                      {initials(u)}
                                    </span>
                                    {u.firstName || userName(u)}
                                  </>
                                ) : (
                                  'Unknown'
                                );
                              })()
                            ) : (
                              <>
                                <span className="task-card-bucket-avatars">
                                  {ids.slice(0, 3).map((id) => {
                                    const u = userById.get(id);
                                    return (
                                      <span
                                        key={id}
                                        className="user-avatar tiny"
                                        style={{ background: avatarColor(id) }}
                                      >
                                        {u ? initials(u) : '?'}
                                      </span>
                                    );
                                  })}
                                  {ids.length > 3 && (
                                    <span className="more">+{ids.length - 3}</span>
                                  )}
                                </span>
                                <span className="task-card-bucket-label">
                                  bucket of {ids.length}
                                </span>
                              </>
                            )}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}
